import TicketLink from './TicketLink';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { money } from '../lib/money';
import { hours as hrs } from '../lib/hours';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';


/**
 * A contractor's billing queue: finished tickets that have not yet been put on
 * an invoice. They select the ones a given invoice covers, enter their own
 * invoice reference, and the tickets drop off the list. NCMB can then look the
 * reference up to see exactly what it covered.
 */
export default function ContractorBilling({ contractorId, token }) {
  // "Bearer null" is refused outright, so an administrator using "View as"
  // saw an empty billing tab rather than the contractor's. Sending nothing
  // lets the interceptor attach their own staff token.
  const auth = useMemo(
    () => (token ? { headers: { Authorization: `Bearer ${token}` } } : undefined),
    [token]);

  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState({});
  const [hours, setHours] = useState({});
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  // The tickets behind the "waiting for approval" count, and whether that box
  // is showing them. Folded away by default: most of the time the number is
  // the whole answer, and this is not the list being worked from.
  const [awaiting, setAwaiting] = useState([]);
  const [showAwaiting, setShowAwaiting] = useState(false);
  const [done, setDone] = useState(null);
  const [nudging, setNudging] = useState(false);

  const load = useCallback(async () => {
    if (!contractorId) return;
    setLoading(true);
    try {
      // ?explain=1 also returns the counts behind the list, so an empty table
      // can say which of the three gates the tickets failed rather than leaving
      // "nothing to bill" to mean anything from no assignment to a broken query.
      // With the token. Without it this asked anonymously, and the server had
      // no way to know the caller was the contractor whose tickets these are.
      const res = await axios.get(
        `${API_BASE}/contractors/${contractorId}/billable-tickets?explain=1`, auth);
      const payload = res.data;
      const rows = Array.isArray(payload) ? payload : (payload?.tickets ?? []);
      setTickets(rows);
      setBreakdown(Array.isArray(payload) ? null : (payload?.breakdown ?? null));
      setAwaiting(Array.isArray(payload?.awaiting) ? payload.awaiting : []);
      setError(payload?.error || null);
      setSelected({});
      // The approver has already agreed a number of hours for each of these,
      // and that number is what should go on the invoice. Typing it back in by
      // hand was a chance to get it wrong for no gain. It stays editable,
      // because some jobs carry a minimum the contractor charges instead.
      setHours(Object.fromEntries(rows
        .filter((t) => t.approved_hours != null)
        .map((t) => [t.id, String(Number(t.approved_hours))])));
    } catch (err) {
      console.error('Could not load billable tickets:', err);
      setError('Could not load your billable tickets');
    } finally { setLoading(false); }
  }, [contractorId, auth]);

  useEffect(() => { load(); }, [load]);

  /**
   * Ask the approvers to look at everything currently waiting.
   *
   * One message per approver covering all their pending tickets, rather than
   * an email each time a job is finished - which is what Gordon asked for, and
   * what keeps the thing from becoming noise people stop reading.
   */
  const askApprovers = async () => {
    setNudging(true); setError(null); setDone(null);
    try {
      const res = await axios.post(
        `${API_BASE}/contractors/${contractorId}/request-approval`, {}, auth);
      setDone(res.data?.message || 'The approvers have been asked to take a look.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not contact the approvers');
    } finally { setNudging(false); }
  };

  const chosen = tickets.filter((t) => selected[t.id]);
  const total = chosen.reduce(
    (sum, t) => sum + (Number(hours[t.id]) || 0) * Number(t.hourly_rate || 0), 0);

  const allChosen = tickets.length > 0 && chosen.length === tickets.length;
  const toggleAll = () =>
    setSelected(allChosen ? {} : Object.fromEntries(tickets.map((t) => [t.id, true])));

  const markBilled = async () => {
    setBusy(true); setError(null); setDone(null);
    try {
      const res = await axios.post(
        `${API_BASE}/contractors/${contractorId}/billing/mark-billed`,
        {
          ticket_ids: chosen.map((t) => t.id),
          invoice_number: invoiceNumber.trim(),
          hours_by_ticket: Object.fromEntries(
            chosen.map((t) => [t.id, Number(hours[t.id]) || 0]))
        },
        auth
      );
      setDone(`${res.data.entries.length} ticket${res.data.entries.length === 1 ? '' : 's'} billed against ${res.data.invoice_number}`);
      setInvoiceNumber('');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not record those as billed');
    } finally { setBusy(false); }
  };

  // The export endpoint sets Content-Disposition; fetch as a blob so the
  // bearer header still goes with it.
  const exportAs = async (format) => {
    setBusy(true); setError(null);
    try {
      const res = await axios.get(
        `${API_BASE}/contractors/${contractorId}/billable-tickets/export`,
        { ...auth, params: { format }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `billable-tickets-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Could not download the export');
    } finally { setBusy(false); }
  };

  if (loading) return <p className="no-data">Loading your billable tickets…</p>;

  return (
    <div className="stack">
      {error && <p className="contractor-error" role="alert">{error}</p>}
      {done && <p className="contractor-ok" role="status">{done}</p>}

      {/* Submitted, not yet decided. Without this the contractor sees work
          vanish from "ready to bill" with no explanation of where it went -
          which is exactly the sort of silence that produces a phone call. */}
      {breakdown?.awaiting_approval > 0 && (
        <section className="card">
          <div className="card-header">
            <div>
              <h3>Waiting for approval</h3>
              <p className="help-text">
                {breakdown.awaiting_approval} ticket
                {breakdown.awaiting_approval === 1 ? '' : 's'} submitted and locked
                until an approver agrees the hours. They cannot be invoiced yet,
                and you cannot change the time on them meanwhile.
              </p>
            </div>
            <div className="card-actions">
              {awaiting.length > 0 && (
                <button type="button" className="btn btn-outline btn-sm"
                  aria-expanded={showAwaiting}
                  onClick={() => setShowAwaiting((v) => !v)}>
                  {showAwaiting ? 'Hide the list' : 'Show which ones'}
                </button>
              )}
              <button type="button" className="btn btn-primary btn-sm"
                onClick={askApprovers} disabled={nudging}>
                {nudging ? 'Sending\u2026' : 'Ask the approvers'}
              </button>
            </div>
          </div>

          {showAwaiting && awaiting.length > 0 && (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Ticket</th>
                    <th>Finished</th>
                    <th>Submitted</th>
                    <th>With</th>
                    <th className="num">Hours claimed</th>
                    <th className="num">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {awaiting.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <div className="nm">
                          <TicketLink id={t.id}><strong>{t.ticket_number}</strong></TicketLink>
                        </div>
                        <div className="sub">{t.title}</div>
                      </td>
                      <td>{t.resolved_at ? String(t.resolved_at).slice(0, 10) : '\u2014'}</td>
                      <td>{t.submitted_at ? String(t.submitted_at).slice(0, 10) : '\u2014'}</td>
                      {/* No named approver means it went to every
                          administrator, which is the rule for a ticket with
                          nobody set on it. */}
                      <td>{t.approver_name || 'Any administrator'}</td>
                      <td className="num">
                        {t.hours_claimed == null ? '\u2014' : hrs(t.hours_claimed)}
                      </td>
                      <td className="num">
                        {t.hours_claimed == null
                          ? '\u2014'
                          : money(Number(t.hours_claimed) * Number(t.hourly_rate || 0))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section className="card">
        <div className="card-header">
          <div>
            <h3>Ready to bill</h3>
            <p className="help-text">
              Work an approver has signed off, not yet on an invoice. Hours have
              to be approved before they can be billed.
            </p>
          </div>
          <div className="card-actions">
            <button type="button" className="btn btn-outline btn-sm"
              onClick={() => exportAs('csv')} disabled={busy || tickets.length === 0}>
              Export CSV
            </button>
            <button type="button" className="btn btn-outline btn-sm"
              onClick={() => exportAs('xlsx')} disabled={busy || tickets.length === 0}>
              Export Excel
            </button>
          </div>
        </div>

        {tickets.length === 0 ? (
          <div className="no-data">
            <strong>Nothing waiting to be billed</strong>
            {/* Three gates, in the order a ticket clears them. Naming the one
                that stopped it is the whole point of this block. */}
            {!breakdown ? (
              <p style={{ marginTop: '0.4rem' }}>
                Tickets appear here once they name this contractor, are resolved
                or closed, and are not already on an invoice.
              </p>
            ) : breakdown.assigned === 0 ? (
              <p style={{ marginTop: '0.4rem' }}>
                No tickets name this contractor. Billing reads the ticket&rsquo;s
                <em> Contractor</em> field &mdash; a ticket assigned to a member of
                staff uses <em>Assignee</em> instead and will not reach billing
                until a contractor is set on it as well.
              </p>
            ) : breakdown.finished === 0 ? (
              <p style={{ marginTop: '0.4rem' }}>
                {breakdown.assigned} ticket{breakdown.assigned === 1 ? '' : 's'} assigned,
                but none resolved or closed yet. Work is billed once it is finished.
              </p>
            ) : (
              <p style={{ marginTop: '0.4rem' }}>
                {breakdown.finished} finished ticket{breakdown.finished === 1 ? '' : 's'},
                {' '}{breakdown.invoiced} already on an invoice. Nothing new to bill.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '2.5rem' }}>
                      <input type="checkbox" checked={allChosen} onChange={toggleAll}
                        aria-label="Select all tickets" />
                    </th>
                    <th>Ticket</th>
                    <th>Resolved</th>
                    <th className="num">Rate</th>
                    {/* What the approver actually signed off, straight from
                        the approval record, beside the box that decides what
                        gets invoiced. They are usually the same number; when
                        they are not, that is a thing worth being able to see. */}
                    <th className="num">Approved</th>
                    <th className="num" style={{ width: '7rem' }}>Hours to bill</th>
                    <th className="num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((t) => {
                    const h = Number(hours[t.id]) || 0;
                    return (
                      <tr key={t.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={!!selected[t.id]}
                            onChange={(e) => setSelected({ ...selected, [t.id]: e.target.checked })}
                            aria-label={`Select ${t.ticket_number}`}
                          />
                        </td>
                        <td>
                          <div className="nm">
                          <TicketLink id={t.id}><strong>{t.ticket_number}</strong></TicketLink>
                        </div>
                          <div className="sub">{t.title}</div>
                        </td>
                        <td>{t.resolved_at ? String(t.resolved_at).slice(0, 10) : '—'}</td>
                        <td className="num">{money(t.hourly_rate)}</td>
                        <td className="num">
                          {t.approved_hours == null ? '—' : hrs(t.approved_hours)}
                        </td>
                        <td className="num">
                          <input
                            type="number" min="0" step="0.25" value={hours[t.id] ?? ''}
                            placeholder="0"
                            onChange={(e) => setHours({ ...hours, [t.id]: e.target.value })}
                            style={{ width: '5.5rem', textAlign: 'right' }}
                            aria-label={`Hours to bill for ${t.ticket_number}`}
                          />
                          {/* Says so out loud rather than leaving the
                              difference to be noticed. A minimum charge is a
                              legitimate reason to bill more than was worked;
                              a typo is not, and they look identical. */}
                          {t.approved_hours != null
                            && Math.abs(h - Number(t.approved_hours)) > 0.009 && (
                            <div className="sub" style={{ textAlign: 'right' }}>
                              {h > Number(t.approved_hours) ? 'over' : 'under'} approved
                            </div>
                          )}
                        </td>
                        <td className="num">{money(h * Number(t.hourly_rate || 0))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="inline-form" style={{ marginTop: '1rem' }}>
              <div className="form-group">
                <label htmlFor="invoice-no">Your invoice number</label>
                <input
                  id="invoice-no" type="text" value={invoiceNumber}
                  placeholder="e.g. 2026-114"
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                />
                <span className="help-text">
                  The reference on your own invoice. NCMB uses it to see what you billed for.
                </span>
              </div>
              <div className="form-group">
                <label>Selected</label>
                <p style={{ fontVariantNumeric: 'tabular-nums', paddingTop: '0.4rem' }}>
                  {chosen.length} ticket{chosen.length === 1 ? '' : 's'} &middot; <strong>{money(total)}</strong>
                </p>
              </div>
              <button
                type="button" className="btn btn-primary"
                onClick={markBilled}
                disabled={busy || chosen.length === 0 || !invoiceNumber.trim()}
              >
                Mark as billed
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
